import { Autocomplete, TextField } from '@mui/material';
import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import useDebounce from '~/hooks/useDebounce';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon } from '~/assets/icons';
import styles from './BaseAutocomplete.module.scss';
import { styled, lighten, darken } from '@mui/system';
import { ICommonSearchItem } from '~/utils/interface/commonField';

const cx = classNames.bind(styles);
const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: lighten(theme.palette.primary.light, 0.85),
  ...theme.applyStyles('dark', {
    backgroundColor: darken(theme.palette.primary.main, 0.8),
  }),
}));

const GroupItems = styled('ul')({
  padding: 0,
});

type AutocompleteProps = {
  options: ICommonSearchItem[];
  onSearch: (...event: any[]) => void;
  onSelected: (...event: any[]) => void;
  onChangeText?: (text: string) => void;
  isOptionEqualToValue?: ICommonSearchItem | null;
  value?: ICommonSearchItem | null;
};

const BaseAutocomplete = (props: AutocompleteProps) => {
  const { options, onSearch } = props;
  const { t } = useTranslation(['admin/layout']);
  const [searchInput, setSearchInput] = useState<string>('');
  const searchInputDebounce = useDebounce(searchInput);
  const [data, setData] = useState<ICommonSearchItem[]>(options);

  useEffect(() => {
    setData(options);
  }, [options]);

  useEffect(() => {
    if (typeof searchInputDebounce !== 'undefined') {
      if (searchInputDebounce.length >= 3) onSearch(searchInputDebounce);
      else setData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInputDebounce]);

  return (
    <Autocomplete
      options={data}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.name}
      noOptionsText={t('noSearchResultsFound')}
      forcePopupIcon={false}
      filterOptions={(options) => options}
      value={props.value}
      isOptionEqualToValue={(option, value) => option.name === value.name}
      onInputChange={(event, newValue) => {
        setSearchInput(newValue);
        props.onChangeText?.(newValue);
      }}
      onChange={(event, newValue) => {
        if (newValue) props.onSelected(newValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          className={cx('search-input')}
          placeholder={t('header.search')}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <React.Fragment>
                 {params.InputProps.endAdornment}
                <SearchIcon className='w-[16px]' />
              </React.Fragment>
            ),
          }}
        />
      )}
      renderGroup={(params) => (
        <li key={params.key}>
          <GroupHeader>{params.group.toUpperCase()}</GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
    />
  );
};
export default memo(BaseAutocomplete);
